import { Copy, Percent } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/marketing/lib/utils';
import { Badge } from '@/marketing/components/ui/badge';
import { Button } from '@/marketing/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/marketing/components/ui/table';
import type { CommissionTierTrio, InfluencerVoucher } from '@/marketing/models/voucher';
import { formatCurrency, formatDate } from '@/marketing/lib/formatters';
import { voucherCampaignLabel } from '@/marketing/lib/voucherCampaignLabel';

const COLUMN_COUNT = 16;

// The table scrolls sideways on most screens; pinning the actions keeps Rates / Disable reachable
// without the admin discovering the scroll first.
const STICKY_ACTIONS_CLASS = 'tw-sticky tw-right-0 tw-bg-background tw-shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]';

function VoucherStatusBadge({ status }: { status: InfluencerVoucher['status'] }) {
  return status === 'active' ? (
    <Badge className="tw-bg-green-100 tw-text-green-700 hover:tw-bg-green-100">Active</Badge>
  ) : (
    <Badge className="tw-bg-muted tw-text-muted-foreground hover:tw-bg-muted">Disabled</Badge>
  );
}

const hasRateOverride = (voucher: InfluencerVoucher): boolean =>
  voucher.commissionPercent !== undefined || voucher.utmPercent !== undefined || voucher.couponUtmPercent !== undefined;

// One effective rate: the code's own override, or the program default it inherits (dimmed).
function RateCell({ override, programDefault }: { override: number | undefined; programDefault: number | undefined }) {
  const percent = override ?? programDefault;

  return (
    <span
      title={override === undefined ? 'program default' : 'custom override'}
      className={cn('tw-tabular-nums', override === undefined ? 'tw-text-muted-foreground' : 'tw-font-semibold')}
    >
      {percent !== undefined ? `${percent}%` : '—'}
    </span>
  );
}

interface VoucherTableProps {
  vouchers: InfluencerVoucher[];
  currency: string;
  tierDefaults: CommissionTierTrio | null;
  selectedCode: string | null;
  /** Code currently being written to, so its row actions stay disabled until the mutation settles. */
  pendingCode: string | null;
  onSelect: (voucher: InfluencerVoucher) => void;
  onToggleStatus: (voucher: InfluencerVoucher) => void;
  onEditCommission: (voucher: InfluencerVoucher) => void;
}

export function VoucherTable({
  vouchers,
  currency,
  tierDefaults,
  selectedCode,
  pendingCode,
  onSelect,
  onToggleStatus,
  onEditCommission,
}: VoucherTableProps) {
  const copyCode = (event: React.MouseEvent, code: string) => {
    event.stopPropagation();
    void navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const runRowAction = (event: React.MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Creator</TableHead>
          <TableHead>Campaign</TableHead>
          <TableHead className="tw-text-right">Discount</TableHead>
          <TableHead className="tw-text-right">Coupon %</TableHead>
          <TableHead className="tw-text-right">Link %</TableHead>
          <TableHead className="tw-text-right">Link+code %</TableHead>
          <TableHead className="tw-text-right">Uses</TableHead>
          <TableHead className="tw-text-right">Orders</TableHead>
          <TableHead className="tw-text-right">Clicks</TableHead>
          <TableHead className="tw-text-right">Link orders</TableHead>
          <TableHead className="tw-text-right">Revenue</TableHead>
          <TableHead className="tw-text-right">Commission {currency}</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className={`tw-text-right ${STICKY_ACTIONS_CLASS}`}>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {vouchers.length === 0 && (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="tw-py-10 tw-text-center tw-text-muted-foreground">
              No codes match the current filters.
            </TableCell>
          </TableRow>
        )}
        {vouchers.map((voucher) => (
          <TableRow
            key={voucher.code}
            onClick={() => onSelect(voucher)}
            className={cn('tw-cursor-pointer', selectedCode === voucher.code && 'tw-bg-muted/60')}
          >
            <TableCell>
              <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                <code className="tw-rounded tw-border tw-border-border tw-bg-muted tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-xs tw-font-semibold">
                  {voucher.code}
                </code>
                <button
                  type="button"
                  aria-label={`Copy ${voucher.code}`}
                  onClick={(event) => copyCode(event, voucher.code)}
                  className="tw-text-muted-foreground hover:tw-text-foreground"
                >
                  <Copy className="tw-h-3.5 tw-w-3.5" />
                </button>
                {hasRateOverride(voucher) && (
                  <Badge className="tw-bg-violet-100 tw-text-violet-700 hover:tw-bg-violet-100">custom</Badge>
                )}
              </span>
              {voucher.source === 'imported' && (
                <div className="tw-mt-0.5 tw-text-[11px] tw-text-muted-foreground">imported</div>
              )}
            </TableCell>
            <TableCell>
              <div className="tw-font-medium">
                @{voucher.handle}
                <span className="tw-ml-1.5 tw-text-xs tw-font-normal tw-text-muted-foreground">{voucher.platform}</span>
              </div>
              {voucher.userId === undefined ? (
                <div className="tw-text-xs tw-text-amber-600">Unclaimed — no Creator Hub account yet</div>
              ) : (
                <div className="tw-text-xs tw-text-muted-foreground">
                  {voucher.displayName ?? `user ${voucher.userId}`}
                </div>
              )}
            </TableCell>
            <TableCell className={voucher.campaignId ? undefined : 'tw-text-muted-foreground'}>
              {voucherCampaignLabel(voucher)}
            </TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">{voucher.discountPercent}%</TableCell>
            <TableCell className="tw-text-right">
              <RateCell override={voucher.commissionPercent} programDefault={tierDefaults?.couponPercent} />
            </TableCell>
            <TableCell className="tw-text-right">
              <RateCell override={voucher.utmPercent} programDefault={tierDefaults?.utmPercent} />
            </TableCell>
            <TableCell className="tw-text-right">
              <RateCell override={voucher.couponUtmPercent} programDefault={tierDefaults?.couponUtmPercent} />
            </TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">{voucher.uses}</TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">{voucher.orderCount}</TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">{voucher.clicks ?? '—'}</TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">{voucher.utmOrders ?? '—'}</TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">
              {formatCurrency(voucher.revenueTotal, currency)}
            </TableCell>
            <TableCell className="tw-text-right tw-tabular-nums">
              {formatCurrency(voucher.commissionTotal, currency)}
            </TableCell>
            <TableCell>
              <VoucherStatusBadge status={voucher.status} />
            </TableCell>
            <TableCell className="tw-text-muted-foreground">{formatDate(voucher.createdAt)}</TableCell>
            <TableCell className={`tw-text-right ${STICKY_ACTIONS_CLASS}`}>
              <div className="tw-flex tw-justify-end tw-gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={pendingCode === voucher.code}
                  onClick={(event) => runRowAction(event, () => onEditCommission(voucher))}
                >
                  <Percent className="tw-mr-1 tw-h-3.5 tw-w-3.5" />
                  Rates
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pendingCode === voucher.code}
                  onClick={(event) => runRowAction(event, () => onToggleStatus(voucher))}
                >
                  {voucher.status === 'active' ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
